import { motion } from "framer-motion";
import { Star } from "lucide-react";
import YoutubeIconSVG from "@/assets/svg/youtube-icon.svg";
import BadgeCheckSVG from "@/assets/svg/badge-check.svg";
import { useEffect, useState } from "react";

export default function Header() {
  const [showRest, setShowRest] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setShowRest(true), 800);
    return () => clearTimeout(timer);
  }, []);

  // Staggered animation variants for smooth reveal
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.3,
        delayChildren: 0.4,
      },
    },
  };

  const itemVariants = {
    hidden: { 
      opacity: 0, 
      y: 30,
      scale: 0.95
    },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: 2,
        ease: [0.22, 1, 0.36, 1],
      },
    },
  };

  const textVariants = {
    hidden: { 
      opacity: 0, 
      y: 40,
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 1.5,
        ease: [0.22, 1, 0.36, 1],
      },
    },
  };

  // Letter variants for stagger animation
  const letterVariants = {
    hidden: { 
      opacity: 0,
    },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.2,
        ease: [0.22, 1, 0.36, 1],
      },
    },
  };

  // Container variants for staggering letters
  const headingSpanContainerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.02,
        delayChildren: 0.05,
      },
    },
  };

  return (
    <div className="relative py-20 xxs:py-32 lg:py-44 4xl:py-[230px] px-4 xxs:px-8 xs:px-10 sm:px-10 md:px-10 lg:px-6 xl:px-8 4xl:px-16 flex item-center justify-center overflow-hidden">
      {/* Background Image */}
      <motion.div
        className="absolute inset-0 w-full h-full bg-cover bg-center bg-no-repeat z-[5] opacity-80"
        style={{
          backgroundImage: `url('https://cdn-vvv-e9hucnd2c4dye2ek.z01.azurefd.net/assets/enfinity/case-study-bg.png')`,
          backgroundPosition: 'center',
          backgroundSize: 'cover',
        }}
        initial={{ opacity: 0, scale: 1.1 }}
        animate={{ opacity: 0.8, scale: 1 }}
        transition={{ duration: 3.5, ease: [0.22, 1, 0.36, 1] }}
      />

      {/* Dark Overlay */}
      <motion.div 
        className="absolute inset-0 w-full h-full bg-black/40 z-[2]"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 2.5, ease: [0.22, 1, 0.36, 1] }}
      ></motion.div>
      
      <motion.div 
        className="mx-auto flex flex-col items-center gap-8 item-center justify-center relative z-10"
        variants={containerVariants}
        initial="hidden"
        animate={showRest ? "visible" : "hidden"}
      >
        {/* YouTube Badge */}
        <motion.div 
          className="flex justify-center items-center z-20 w-full"
          variants={itemVariants}
        >
          <a
            href="https://servicesdirectory.withyoutube.com/directory/enfinity-talent-agency/"
            target="_blank"
            rel="noopener noreferrer"
          >
            <div className="relative h-14 w-fit overflow-hidden">
              <div className="absolute flex items-center gap-2 rounded-full py-1 px-4 border border-white/20 bg-white/5 backdrop-blur-md text-white shimmer-badge">
                <img
                  src={YoutubeIconSVG}
                  alt="Official YouTube Certified"
                  className="h-8 w-8"
                />
                <span className="xxs:text-xs xs:text-sm text-base">Official YouTube Certified</span>
                <img src={BadgeCheckSVG} className="h-5 w-5" alt="Badge" />
              </div>
            </div>
          </a>
        </motion.div>

        {/* Main Heading */}
        <motion.h1 
          className="text-5xl xxs:text-[55px] lg:text-[80px] 4xl:text-[90px] font-normal text-white text-center leading-[106px] xxs:leading-normal xs:leading-normal sm:leading-normal md:leading-normal lg:leading-[106px] enfinity-header-font"
        >
          <motion.span 
            className="block"
            variants={headingSpanContainerVariants}
            initial="hidden"
            animate={showRest ? "visible" : "hidden"}
          >
            {"Talk to the Enfinity Team".split(" ").map((word, wordIndex) => (
              <span key={`heading-word-${wordIndex}`} style={{ display: "inline-block", whiteSpace: "nowrap", marginRight: "0.25em" }} className="text-white">
                {word.split("").map((char, charIndex) => (
                  <motion.span
                    key={`heading-${wordIndex}-${charIndex}`}
                    variants={letterVariants}
                    style={{ display: "inline-block" }}
                  >
                    {char}
                  </motion.span>
                ))}
              </span>
            ))}
          </motion.span>
        </motion.h1>

        {/* Subtitle */}
        <motion.p 
          className="text-2xl xxs:text-[1rem] lg:text-base font-normal text-white text-center max-w-[978px] leading-normal enfinity-subtext-font"
          variants={itemVariants}
        >
          Have questions? We're here to help. Reach out to our team and we'll get back to you as soon as possible.
        </motion.p>
      </motion.div>
    </div>
  );
}

